// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Select, Button, Table, Tag, Timeline, message, Spin, Modal, Space, DatePicker, Skeleton } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, EnvironmentOutlined, CalendarOutlined, CarOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Title } from '../components/design-system/typography';
import { StatusPill } from '../components/design-system/StatusPill';
import { colors, spacing } from '../components/design-system/tokens';
import { queryConfigs, queryKeys } from '../utils/queryConfig';
import { useQuery } from '@tanstack/react-query';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface ContainerEvent {
  id: string;
  timestamp: string;
  location: string;
  event: string;
  eventCode: string;
  description: string;
  vessel?: string;
  voyage?: string;
}

interface ContainerData {
  id: string;
  containerNumber: string;
  sealNumber: string;
  contractRef: string;
  shipmentRef: string;
  supplier: string;
  buyer: string;
  pol: string;
  pod: string;
  vessel: string;
  voyage: string;
  eta: string;
  etd: string;
  status: 'EMPTY' | 'LOADING' | 'FULL' | 'IN_TRANSIT' | 'DISCHARGED' | 'DELIVERED';
  lastLocation: string;
  lastUpdate: string;
  commodity: string;
  weight: number;
  shippingLine: string;
  events: ContainerEvent[];
}

interface ContainerResponse {
  containers: ContainerData[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

interface FilterOptions {
  ports: {
    pol: string[];
    pod: string[];
  };
  shippingLines: string[];
  statuses: string[];
}

// API client functions
const fetchContainers = async (filters: {
  pol?: string;
  pod?: string;
  status?: string;
  shippingLine?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}): Promise<ContainerResponse> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');

  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && value !== 'all') {
      params.append(key, value);
    }
  });

  const response = await fetch(`/api/v1/tracking/containers?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch container data');
  }

  return response.json();
};

const fetchFilterOptions = async (): Promise<FilterOptions> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');

  const response = await fetch('/api/v1/tracking/filters', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch filter options');
  }

  return response.json();
};

const ContainerTracking: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [polFilter, setPolFilter] = useState<string>('all');
  const [podFilter, setPodFilter] = useState<string>('all');
  const [shippingLineFilter, setShippingLineFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<ContainerData | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Build filters for API
  const filters = {
    search: searchText || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    pol: polFilter !== 'all' ? polFilter : undefined,
    pod: podFilter !== 'all' ? podFilter : undefined,
    shippingLine: shippingLineFilter !== 'all' ? shippingLineFilter : undefined,
    startDate: dateRange?.[0] || undefined,
    endDate: dateRange?.[1] || undefined,
  };

  // Fetch containers with React Query
  const { data: containerResponse, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.tracking.containersList(filters),
    queryFn: () => fetchContainers(filters),
    ...queryConfigs.realTimeData, // Container tracking is real-time data
  });

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.tracking.filters(),
    queryFn: fetchFilterOptions,
    ...queryConfigs.staticData, // Filter options are static data
  });

  const containers = containerResponse?.containers || [];
  const loading = isLoading;

  // Show error message if API call fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load container data: ${error.message}`);
    }
  }, [error]);

  const getStatusVariant = (status: ContainerData['status']) => {
    switch (status) {
      case 'DELIVERED':
        return 'positive';
      case 'IN_TRANSIT':
        return 'warning';
      case 'DISCHARGED':
        return 'positive';
      case 'LOADING':
      case 'FULL':
        return 'neutral';
      case 'EMPTY':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getStatusText = (status: ContainerData['status']) => {
    switch (status) {
      case 'EMPTY':
        return 'Empty';
      case 'LOADING':
        return 'Loading';
      case 'FULL':
        return 'Full';
      case 'IN_TRANSIT':
        return 'In Transit';
      case 'DISCHARGED':
        return 'Discharged';
      case 'DELIVERED':
        return 'Delivered';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatWeight = (weight: number) => {
    return `${weight.toLocaleString()} kg`;
  };

  const getCarrierColor = (shippingLine: string) => {
    const colors: { [key: string]: string } = {
      'ONE': '#00A6D6',
      'MSC': '#01539B', 
      'HMM': '#E31837',
      'YML': '#1E3A5F',
      'EMC': '#ED8B00',
      'CMA': '#009FE3'
    };
    return colors[shippingLine] || '#666666';
  };

  const showContainerDetails = (container: ContainerData) => {
    setSelectedContainer(container);
    setModalVisible(true);
  };

  const columns: ColumnsType<ContainerData> = [
    {
      title: 'Container Number',
      dataIndex: 'containerNumber',
      key: 'containerNumber',
      width: 140,
      render: (text: string, record: ContainerData) => (
        <div>
          <div style={{ fontWeight: 600, color: colors.text.primary, fontFamily: 'monospace' }}>
            {text}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
            {record.contractRef}
          </div>
        </div>
      ),
    },
    {
      title: 'Route',
      key: 'route',
      width: 200,
      render: (_, record: ContainerData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.pol}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, margin: '2px 0' }}>
            ↓
          </div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.pod}
          </div>
        </div>
      ),
    },
    {
      title: 'Vessel & Voyage',
      key: 'vessel',
      width: 180,
      render: (_, record: ContainerData) => (
        <div>
          <div style={{ 
            fontWeight: 500, 
            color: getCarrierColor(record.shippingLine), 
            fontSize: '13px' 
          }}>
            {record.vessel}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
            Voyage: {record.voyage}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.tertiary }}>
            {record.shippingLine}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ContainerData['status']) => (
        <StatusPill variant={getStatusVariant(status)}>
          {getStatusText(status)}
        </StatusPill>
      ),
    },
    {
      title: 'Last Location',
      dataIndex: 'lastLocation',
      key: 'lastLocation',
      width: 200,
      render: (location: string, record: ContainerData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            <EnvironmentOutlined style={{ marginRight: '4px' }} />
            {location}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
            Updated: {formatDateTime(record.lastUpdate)}
          </div>
        </div>
      ),
    },
    {
      title: 'Commodity',
      key: 'details',
      width: 150,
      render: (_, record: ContainerData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.commodity}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
            {formatWeight(record.weight)}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record: ContainerData) => (
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={() => showContainerDetails(record)}
          title="View Tracking Details"
        />
      ),
    },
  ];

  // Create status options from API data
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    ...statusOptionsFromAPI.map(status => ({
      value: status,
      label: status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' '),
    })),
  ];

  const getStatusCounts = () => {
    const counts = {
      total: containers.length,
      inTransit: containers.filter(c => c.status === 'IN_TRANSIT').length,
      loading: containers.filter(c => c.status === 'LOADING').length,
      discharged: containers.filter(c => c.status === 'DISCHARGED').length,
      delivered: containers.filter(c => c.status === 'DELIVERED').length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Get filter options from API
  const polPorts = filterOptions?.ports.pol || [];
  const podPorts = filterOptions?.ports.pod || [];
  const shippingLines = filterOptions?.shippingLines || [];
  const statusOptionsFromAPI = filterOptions?.statuses || ['EMPTY', 'LOADED', 'IN_TRANSIT', 'DELIVERED'];

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: spacing['2xl'] 
        }}>
          <div>
            <Title>Container Tracking</Title>
            <p style={{
              color: colors.text.secondary,
              fontSize: '16px',
              marginTop: spacing.sm
            }}>
              Real-time tracking of all containers across active shipments
            </p>
          </div>
          
          <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh Data
          </Button>
        </div>

        {/* Status Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: spacing.lg,
          marginBottom: spacing['2xl']
        }}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton.Input style={{ width: 60, height: 24 }} active />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 600, color: colors.text.primary }}>
                  {statusCounts.total}
                </div>
              )}
              <div style={{ color: colors.text.secondary, marginTop: '4px' }}>Total Containers</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton.Input style={{ width: 60, height: 24 }} active />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.warning }}>
                  {statusCounts.inTransit}
                </div>
              )}
              <div style={{ color: colors.text.secondary, marginTop: '4px' }}>In Transit</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton.Input style={{ width: 60, height: 24 }} active />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.neutral }}>
                  {statusCounts.loading}
                </div>
              )}
              <div style={{ color: colors.text.secondary, marginTop: '4px' }}>Loading</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton.Input style={{ width: 60, height: 24 }} active />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.positive }}>
                  {statusCounts.discharged}
                </div>
              )}
              <div style={{ color: colors.text.secondary, marginTop: '4px' }}>Discharged</div>
            </div>
          </Card>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton.Input style={{ width: 60, height: 24 }} active />
              ) : (
                <div style={{ fontSize: '24px', fontWeight: 600, color: colors.status.positive }}>
                  {statusCounts.delivered}
                </div>
              )}
              <div style={{ color: colors.text.secondary, marginTop: '4px' }}>Delivered</div>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card style={{ marginBottom: spacing.lg }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: spacing.lg,
            alignItems: 'center',
            marginBottom: spacing.lg
          }}>
            <Search
              placeholder="Search containers, vessels, routes..."
              allowClear
              style={{ minWidth: 280 }}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            
            <Select
              placeholder="All Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="All POL"
              value={polFilter}
              onChange={setPolFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">All Ports of Loading</Option>
              {polPorts.map(port => (
                <Option key={port} value={port}>{port}</Option>
              ))}
            </Select>

            <Select
              placeholder="All POD"
              value={podFilter}
              onChange={setPodFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">All Ports of Discharge</Option>
              {podPorts.map(port => (
                <Option key={port} value={port}>{port}</Option>
              ))}
            </Select>

            <Select
              placeholder="All Shipping Lines"
              value={shippingLineFilter}
              onChange={setShippingLineFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">All Shipping Lines</Option>
              {shippingLines.map(line => (
                <Option key={line} value={line}>{line}</Option>
              ))}
            </Select>

            <RangePicker
              placeholder={['ETA From', 'ETA To']}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
                } else {
                  setDateRange(null);
                }
              }}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: spacing.sm,
            borderTop: `1px solid ${colors.background.tertiary}`
          }}>
            <Button 
              icon={<ClearOutlined />}
              onClick={() => {
                setSearchText('');
                setStatusFilter('all');
                setPolFilter('all');
                setPodFilter('all');
                setShippingLineFilter('all');
                setDateRange(null);
              }}
            >
              Clear All Filters
            </Button>
            
            <div style={{ fontSize: '13px', color: colors.text.secondary }}>
              Last updated: {new Date().toLocaleTimeString()} • Auto-refresh: 60s
            </div>
          </div>
        </Card>

        {/* Containers Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={containers}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} containers`,
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Card>

        {/* Container Details Modal */}
        <Modal
          title={selectedContainer ? `Container ${selectedContainer.containerNumber}` : 'Container Details'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={800}
        >
          {selectedContainer && (
            <div>
              {/* Container Info */}
              <Row gutter={[16, 16]} style={{ marginBottom: spacing.lg }}>
                <Col span={12}>
                  <Card size="small" title="Container Information">
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <div><strong>Container:</strong> {selectedContainer.containerNumber}</div>
                      <div><strong>Seal:</strong> {selectedContainer.sealNumber}</div>
                      <div><strong>Weight:</strong> {formatWeight(selectedContainer.weight)}</div>
                      <div><strong>Commodity:</strong> {selectedContainer.commodity}</div>
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title="Shipment Details">
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <div><strong>Contract:</strong> {selectedContainer.contractRef}</div>
                      <div><strong>Vessel:</strong> {selectedContainer.vessel}</div>
                      <div><strong>Voyage:</strong> {selectedContainer.voyage}</div>
                      <div><strong>Shipping Line:</strong> {selectedContainer.shippingLine}</div>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Current Status */}
              <Card size="small" title="Current Status" style={{ marginBottom: spacing.lg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
                  <StatusPill variant={getStatusVariant(selectedContainer.status)}>
                    {getStatusText(selectedContainer.status)}
                  </StatusPill>
                  <div style={{ fontSize: '13px', color: colors.text.secondary }}>
                    <EnvironmentOutlined style={{ marginRight: '4px' }} />
                    {selectedContainer.lastLocation}
                  </div>
                </div>
              </Card>

              {/* Tracking Timeline */}
              <Card size="small" title="Tracking Events">
                <Timeline
                  items={selectedContainer.events.map(event => ({
                    children: (
                      <div>
                        <div style={{ fontWeight: 600, color: colors.text.primary }}>
                          {event.event} - {event.location}
                        </div>
                        <div style={{ fontSize: '12px', color: colors.text.secondary, marginTop: '4px' }}>
                          <CalendarOutlined style={{ marginRight: '4px' }} />
                          {formatDateTime(event.timestamp)}
                        </div>
                        {event.vessel && (
                          <div style={{ fontSize: '12px', color: colors.text.secondary }}>
                            <CarOutlined style={{ marginRight: '4px' }} />
                            {event.vessel} - Voyage {event.voyage}
                          </div>
                        )}
                        <div style={{ fontSize: '13px', color: colors.text.primary, marginTop: '4px' }}>
                          {event.description}
                        </div>
                      </div>
                    ),
                    color: event.eventCode === 'DEP' ? colors.status.warning : 
                           event.eventCode === 'ARR' || event.eventCode === 'DIS' ? colors.status.positive :
                           colors.status.neutral
                  }))
                  .reverse()} // Show most recent events first
                />
              </Card>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ContainerTracking;